<template>
  <v-layout>
    <v-flex>
      <v-row>
        <v-col cols="12" class="py-0">
          <v-container fluid py-0>
            <v-row align="center">
              <div>
                <h1 class="headline" v-if="showTitle">Task Tracker</h1>
              </div>
              <v-col class="py-0">
                <list-filters
                  :filters="tasksFilters"
                  label="Search"
                  :ref-name="refName"
                  :cached-filters="cachedFilters"
                  @searchParamsChanged="searchParamsChanged($event)"
                  :expansionPanel="false"
                ></list-filters>
              </v-col>
              <div class="pr-2">
                <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
              </div>
              <v-select
                v-if="showGroupedView"
                :items="groupByOptions"
                v-model="selectedGroupedBy"
                label="Group by"
                item-text="name"
                return-object
                :outlined="outlinedInputs"
                hide-details
                dense
                class="mr-2"
              ></v-select>
              <v-switch
                prepend-icon="mdi-view-list"
                class="mr-3"
                label="Group View"
                v-model="showGroupedView"
              ></v-switch>
              <div>
                <list-visible-columns-and-filter
                  :visible-columns="visibleColumns"
                  :selectable-headers="selectableHeaders"
                  v-model="search"
                  @change="onVisibleColumnsChange"
                ></list-visible-columns-and-filter>
              </div>
              <div v-if="allowNewTaskCreation">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      fab
                      dark
                      color="primary"
                      @click="onCreateNew"
                    >
                      <v-icon dark>mdi-plus</v-icon>
                    </v-btn>
                  </template>
                  <span>Create New Itinerary Task</span>
                </v-tooltip>
              </div>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
      <v-row v-if="showGroupedView">
        <v-col class="py-0">
          <list-grouped :group="selectedGroupedBy" :items="items">
            <template v-slot:table="{ items }">
              <tasks-list-table
                :items="items"
                :headers="filteredHeaders"
                :search="search"
                @delete="deleteItem"
                @edit="editItem"
              />
            </template>
          </list-grouped>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col class="py-0">
          <ag-grid-vue
            v-if="showAgGrid"
            class="ag-theme-alpine"
            id="ag-tasks-list"
            :columnDefs="agGridColumns"
            :rowData="items"
            rowSelection="multiple"
            :context="agContext"
            @grid-ready="onAgGridReady"
            @columnMoved="onAgColumnMoved"
            @sortChanged="onAgSortChanged"
            @selection-changed="onAgSelectionChanged"
            @newColumnsLoaded="onAgNewColumns"
            suppressDragLeaveHidesColumns
          >
          </ag-grid-vue>
          <list-actions
            v-if="showAgGrid && selected.length > 0"
            style="top: -30px; position: relative"
            :selected="selected"
            list="itineraryTask"
            :columns="visibleColumns"
            :filter="searchParams"
          >
          </list-actions>
          <v-data-table
            v-if="!showAgGrid"
            :headers="filteredHeaders"
            :items="items"
            :loading="$apollo.loading"
            :search="search"
            item-key="id"
            class="elevation-1"
            show-select
            v-model="selected"
            v-bind="pagingOptions"
            @update:options="onListOptionsUpdate"
            fixed-header
            hide-default-footer
            :height="gridHeight"
            @current-items="dataTableReady"
          >
            <template v-slot:item.itineraryRef="{ item }">
              <reference-label
                v-model="item.itineraryRef"
                :itinerary-id="item.itineraryId"
              ></reference-label>
            </template>
            <template v-slot:item.project="{ item }">
              {{
                item.taskType == "itinerary"
                  ? item.project.split("/").pop()
                  : item.project
              }}
            </template>
            <template v-slot:item.estimatedStart="{ item }">{{
              item.estimatedStart | formatDate
            }}</template>
            <template v-slot:item.itineraryStartDate="{ item }">{{
              item.itineraryStartDate | formatDate
            }}</template>
            <template v-slot:item.itineraryEndDate="{ item }">{{
              item.itineraryEndDate | formatDate
            }}</template>
            <template v-slot:item.updatedAt="{ item }">{{
              item.updatedAt | formatDate
            }}</template>
            <template v-slot:item.createdAt="{ item }">{{
              item.createdAt | formatDate
            }}</template>
            <template v-slot:item.nextActionDueDate="{ item }">{{
              item.nextActionDueDate | formatDate
            }}</template>
            <template v-slot:item.actions="{ item }">
              <default-list-actions
                :item="item"
                hide-view
                @deleteItem="deleteItem"
                @editItem="editItem"
              >
              </default-list-actions>
            </template>
            <template v-slot:footer>
              <list-actions
                v-if="selected.length > 0"
                :selected="selected"
                list="itineraryTask"
                :columns="visibleColumns"
                :filter="searchParams"
              >
              </list-actions>
            </template>
          </v-data-table>
          <get-more-results
            :first="first"
            @limitChange="onLimitChange"
            :items="itineraryTasks"
            :totalItems="totalItems"
            :loading="$apollo.loading"
            :loadMoreEnabled="loadMoreEnabled"
            @loadMore="loadMore('itineraryTasks')"
          ></get-more-results>
        </v-col>
      </v-row>

      <edit-itinerary-task-dialog
        :show="showItineraryTaskEditor"
        @close="showItineraryTaskEditor = false"
        :itinerary="itinerary"
        v-model="editedItineraryTaskModel"
        @input="onSave"
      ></edit-itinerary-task-dialog>
    </v-flex>
    <div v-if="debug">[{{ items }}]</div>
  </v-layout>
</template>

<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import GetMoreResults from "../../shared/GetMoreResults";
import ListFilters from "../../shared/list-filters/ListFilters";
import { ALL_ITINERARY_TASKS_SEARCH_QUERY } from "../../../../constants/graphql";
import filters from "./filters";
import {
  sortByDate,
  sortByDateWithFormat,
  sortByReference,
} from "../../../../helpers/index";
import StatusLabel from "../../shared/labels/StatusLabel";
import SupplierLabel from "../../shared/labels/SupplierLabel";
import AgentLabel from "../../shared/labels/AgentLabel";
import ReferenceLabel from "../../shared/labels/ReferenceLabel";
import ClientLabel from "../../shared/labels/ClientLabel";
import { AgGridVue } from "ag-grid-vue";
import AgGridSwitch from "../../shared/AgGridSwitch";
import AgGridListMixin from "../../../../mixins/AgGridListMixin";
import ListActions from "../../shared/list-actions/ListActions";
import ListGrouped from "../../shared/ListGrouped.vue";
import EditItineraryTaskDialog from "./EditItineraryTaskDialog";
import gql from "graphql-tag";
import DefaultListActions from "../../shared/DefaultListActions";
import TasksListTable from "./TasksListTable";

const DELETE_MUTATION = gql`
  mutation deleteItineraryTask($id: ID!) {
    deleteItineraryTask(id: $id) {
      id
    }
  }
`;
const CREATE_MUTATION = gql`
  mutation createItineraryTask($input: ItineraryTaskInput!) {
    createItineraryTask(input: $input) {
      id
      name
      assignedToName
      estimatedStart
      itineraryRef
      notes
      project
      projectId
      taskType
    }
  }
`;
const UPDATE_MUTATION = gql`
  mutation updateItineraryTask($id: ID!, $input: ItineraryTaskInput!) {
    updateItineraryTask(id: $id, input: $input) {
      id
      name
      assignedToName
      estimatedStart
      itineraryRef
      notes
      project
      projectId
      taskType
      updatedBy
    }
  }
`;

export default {
  name: "task-tracker-list",

  props: {
    itinerary: {
      type: Object,
    },
    client: {
      type: Object,
    },
    agent: {
      type: Object,
    },
    supplier: {
      type: Object,
    },
    refName: {
      type: String,
      default: () => "taskTrackerFilters",
    },
    showTitle: {
      type: Boolean,
      default: () => true,
    },
    cachedFilters: {
      type: Boolean,
      default: () => true,
    },
    refNameColumns: {
      type: String,
      default: () => "tasks-list-cols",
    },
    showGroupBy: {
      type: Boolean,
      default: () => true,
    },
  },

  mixins: [ListMixin, AgGridListMixin],

  components: {
    DefaultListActions,
    ListVisibleColumnsAndFilter,
    EditItineraryTaskDialog,
    GetMoreResults,
    ListFilters,
    StatusLabel,
    SupplierLabel,
    AgentLabel,
    ReferenceLabel,
    ClientLabel,
    AgGridSwitch,
    AgGridVue,
    ListActions,
    ListGrouped,
    TasksListTable,
  },

  data() {
    return {
      debug: false,
      showGroupedView: false,
      selectedGroupedBy: {
        name: "Priority",
        getKey: (item) => item.priority,
        getLabel: (item) => `Priority: ${item.priority}`,
      },
      groupByOptions: [
        {
          name: "Priority",
          getKey: (item) => item.priority,
          getLabel: (item) => `Priority: ${item.priority}`,
        },
        {
          name: "Due Date",
          getKey: (item) => item.estimatedStart,
          getLabel: (item) =>
            this.$options.filters.formatDate(item.estimatedStart),
        },
        {
          name: "Assigned To",
          getKey: (item) => item.assignedToName,
          getLabel: (item) => item.assignedToName,
        },
      ],
      filters,
      showItineraryTaskEditor: false,
      inItineraryContext: false,
      editedItineraryTaskModel: {},
      itineraryTasks: {},
      selected: [],
      search: "",
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "itineraryRef",
        "estimatedStart",
        "taskStatus",
        "itineraryInternalStatus",
        "assignedToName",
        "assignedByName",
        "actions",
      ]),
      headers: [
        {
          text: "Task Name",
          value: "name",
          name: "name",
          sortable: true,
        },
        {
          text: "Iti Ref",
          value: "itineraryRef",
          name: "itineraryRef",
          sort: sortByReference,
          comparator: sortByReference,
          sortable: true,
          cellRendererFramework: "ReferenceLabel",
        },
        {
          text: "Iti Start Date",
          value: "itineraryStartDate",
          name: "itineraryStartDate",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.itineraryStartDate),
        },
        {
          text: "Due Date",
          value: "estimatedStart",
          name: "estimatedStart",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.estimatedStart),
        },
        {
          text: "Creation Date",
          value: "createdAt",
          name: "createdAt",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.createdAt
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.createdAt),
        },
        {
          text: "Assigned By",
          value: "assignedByName",
          name: "assignedByName",
          sortable: true,
        },
        {
          text: "Assigned To",
          value: "assignedToName",
          name: "assignedToName",
          sortable: true,
        },
        {
          text: "Updated By",
          value: "updatedBy",
          name: "updatedBy",
          sortable: true,
        },
        {
          text: "Type",
          value: "taskType",
          name: "taskType",
          sortable: true,
        },
        {
          text: "Project",
          value: "project",
          name: "project",
          sortable: true,
          valueGetter: (payload) =>
            payload.data.project.toString().includes("/")
              ? payload.data.project.split("/").pop()
              : payload.data.project,
        },
        {
          text: "Status",
          value: "taskStatus",
          name: "taskStatus",
          sortable: true,
        },
        {
          text: "Priority",
          value: "priority",
          name: "priority",
          sortable: true,
        },
        {
          text: "Internal Status",
          value: "itineraryInternalStatus",
          name: "itineraryInternalStatus",
          sortable: true,
        },
        {
          text: "Notes",
          value: "notes",
          name: "notes",
          sortable: true,
        },
        {
          text: "Next Follow up",
          value: "nextAction",
          name: "nextAction",
          sortable: true,
        },
        {
          text: "Next Follow up Due Date",
          value: "nextActionDueDate",
          name: "nextActionDueDate",
          sortable: true,
        },
        {
          text: "Last Updated",
          value: "updatedAt",
          name: "updatedAt",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.updatedAt
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.updatedAt),
        },
        {
          text: "Actions",
          value: "actions",
          name: "actions",
          sortable: false,
          cellRendererFramework: "DefaultListActions",
        },
      ],
    };
  },
  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    customFilters() {
      const additionalFilters = {};
      if (this.itinerary) {
        additionalFilters["itineraryId_eq"] = this.itinerary.id;
      }
      if (this.client) {
        additionalFilters["projectId_eq"] = this.client.id;
        additionalFilters["taskType_eq"] = "client";
      }
      if (this.agent) {
        additionalFilters["projectId_eq"] = this.agent.id;
        additionalFilters["taskType_eq"] = "agent";
      }
      if (this.supplier) {
        additionalFilters["projectId_eq"] = this.supplier.id;
        additionalFilters["taskType_eq"] = "supplier";
      }
      return additionalFilters;
    },

    editable() {
      return true;
    },
    allowNewTaskCreation() {
      return (
        !!this.itinerary || !!this.supplier || !!this.client || !!this.agent
      );
    },
    items() {
      return this.itineraryTasks && this.itineraryTasks.edges
        ? this.itineraryTasks.edges.map(this.mapTasks)
        : [];
    },
    tasksFilters() {
      return filters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
  },
  methods: {
    onCreateNew() {
      this.editedItineraryTaskModel = { name: "New Itinerary Task" };
      if (this.supplier) {
        this.editedItineraryTaskModel = Object.assign(
          this.editedItineraryTaskModel,
          {
            taskType: "supplier",
            project: this.supplier.name,
            projectId: this.supplier.id,
            userId: undefined,
          }
        );
      }
      if (this.client) {
        console.log("this.client", this.client);
        this.editedItineraryTaskModel = Object.assign(
          this.editedItineraryTaskModel,
          {
            taskType: "client",
            project: `${this.client.name} ${this.client.surname}`,
            projectId: this.client.id,
            userId: undefined,
          }
        );
      }
      if (this.agent) {
        this.editedItineraryTaskModel = Object.assign(
          this.editedItineraryTaskModel,
          {
            taskType: "agent",
            project: this.agent.name,
            projectId: this.agent.id,
            userId: undefined,
          }
        );
      }
      this.showItineraryTaskEditor = true;
    },

    editItem(item) {
      this.editedItineraryTaskModel = { ...item };
      this.showItineraryTaskEditor = true;
    },

    async onSave(payload) {
      this.showItineraryTaskEditor = false;
      const { id, ...input } = payload;
      const mutation = payload.id ? UPDATE_MUTATION : CREATE_MUTATION;
      try {
        const mutationRes = await this.$apollo.mutate({
          mutation: mutation,
          variables: { input: { ...input }, id },
        });
        this.$apollo.queries.itineraryTasks.refetch();
        this.$store.dispatch("showAppSnackbar", {
          msg: "Itinerary Task Saved.",
        });
      } catch (error) {
        console.log(error);
      }
    },
    async deleteItem(payload) {
      try {
        await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: {
            id: payload.id,
          },
        });
        this.$apollo.queries.itineraryTasks.refetch();
        this.$store.dispatch("showAppSnackbar", {
          msg: "Itinerary Task deleted",
        });
      } catch (error) {
        console.log(error);
      }
    },

    mapTasks(item) {
      const { user, itinerary, ...task } = item.node;
      const itiData = {};
      if (itinerary) {
        itiData.itineraryId = itinerary.id;
        itiData.itineraryInternalStatus = itinerary.internalStatus;
        itiData.itineraryStartDate = itinerary.startDate;
      }
      const userData = {};
      if (user) {
        userData.userId = user.id;
      }
      return { ...task, ...itiData, ...userData };
    },
  },
  apollo: {
    itineraryTasks: {
      query: ALL_ITINERARY_TASKS_SEARCH_QUERY,
      variables() {
        return {
          after: this.after,
          filter: {
            ...this.searchParams,
            ...this.customFilters,
          },
          first: 50,
          sort: this.sort,
        };
      },
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {
        this.totalItems = data.itineraryTasks.totalCount;
      },
    },
  },
};
</script>

<style>
#ag-tasks-list {
  height: calc(100vh - 320px);
}
</style>



// WEBPACK FOOTER //
// src/components/gt/plugins/task-tracker/TaskTrackerList.vue